/**
 * Reloads a tab that is still running a superseded JS/CSS bundle.
 *
 * Why this exists: `index.html` keeps a stable name while every asset under it
 * is content-hashed. Once a browser has `index.html` in its HTTP cache it stops
 * asking the origin for it, so it keeps resolving the *old* bundle hashes from
 * its own cache. A CloudFront invalidation cannot fix that, because an
 * invalidation only clears the edge cache and never reaches the browser cache.
 * The result is a tab that renders a build from weeks ago and looks like a
 * regression that "won't go away no matter how many times we redeploy".
 *
 * Deploys now send `no-cache, no-store, must-revalidate` for `index.html`
 * (see infra/redeploy.ps1), which prevents new tabs from getting stuck. This
 * guard covers the tabs that were already poisoned before that, and any future
 * cache misconfiguration, by comparing the bundle this tab actually booted with
 * against the one the origin currently advertises.
 *
 * Deliberately runs only at boot: there is no conversation in flight at that
 * point, so a reload cannot discard a half-streamed answer.
 */

const RELOAD_MARKER = 'staleBundleGuard:reloadedFor';

const ENTRY_PATTERN = /\/static\/js\/main\.[^/]+\.js$/;

/** `https://host/static/js/main.abc123.js?x=1` -> `main.abc123.js` */
const fileNameOf = (value: string): string => {
  const withoutQuery = value.split(/[?#]/)[0];
  return withoutQuery.slice(withoutQuery.lastIndexOf('/') + 1);
};

/** The main entry script this tab is currently executing. */
const loadedEntryScript = (): string | null => {
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'));
  const entry = scripts.find((s) => ENTRY_PATTERN.test(s.src.split(/[?#]/)[0]));
  return entry ? fileNameOf(entry.src) : null;
};

/** The main entry script the origin is serving right now. */
const publishedEntryScript = async (): Promise<string | null> => {
  const response = await fetch(`${process.env.PUBLIC_URL || ''}/asset-manifest.json`, {
    cache: 'no-store',
    credentials: 'omit',
  });
  if (!response.ok) return null;

  const manifest: { entrypoints?: string[] } = await response.json();
  const entry = (manifest.entrypoints || []).find((e) => /^static\/js\/main\..+\.js$/.test(e));
  return entry ? fileNameOf(entry) : null;
};

export async function guardAgainstStaleBundle(): Promise<void> {
  // In development the bundle is served unhashed from the dev server, so there
  // is nothing meaningful to compare and a reload loop would be the only effect.
  if (process.env.NODE_ENV !== 'production') return;

  try {
    const loaded = loadedEntryScript();
    if (!loaded) return;

    const published = await publishedEntryScript();
    if (!published) return;

    if (published === loaded) {
      // Up to date. Clear the marker so a *future* deploy can trigger a reload.
      sessionStorage.removeItem(RELOAD_MARKER);
      return;
    }

    // Reload at most once per published version, so a bad deploy (or an origin
    // whose manifest disagrees with the HTML it serves) cannot loop the tab.
    if (sessionStorage.getItem(RELOAD_MARKER) === published) return;
    sessionStorage.setItem(RELOAD_MARKER, published);

    window.location.reload();
  } catch {
    // Offline, blocked request, malformed manifest: never let the guard break boot.
  }
}
